import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { DialogService } from '../../../services/dialog.service';
import { PermissionsService } from '../../../services/permissions.service';
import { StudentInquiryEntityComponent } from '../../forms/student-inquiry-entity/student-inquiry-entity.component';
import { EnrollmentEntityComponent } from '../../forms/enrollment-entity/enrollment-entity.component';
import { PaymentsEntityComponent } from '../../forms/payments-entity/payments-entity.component';
import { EventLessonEntityComponent } from '../../forms/event-lesson-entity/event-lesson-entity.component';
import { LoggerService } from '../../../services/logger.service';

export interface QuickAction {
  icon: string;
  label: string;
  color: string;
  action: () => void;
  permission?: string;
}

@Component({
  selector: 'app-quick-actions',
  templateUrl: './quick-actions.component.html',
  styleUrls: ['./quick-actions.component.css']
})
export class QuickActionsComponent implements OnInit {
  @Output() actionClicked = new EventEmitter<string>();

  quickActions: QuickAction[] = [];

  constructor(
    private router: Router,
    private dialogService: DialogService,
    private permissionsService: PermissionsService
  ) { }

  ngOnInit(): void {
    this.quickActions = [
      {
        icon: 'person_add',
        label: 'Add Inquiry',
        color: 'primary',
        permission: 'students_create',
        action: () => this.openNewInquiry()
      },
      {
        icon: 'event',
        label: 'Schedule Class',
        color: 'accent',
        permission: 'lessons_create',
        action: () => this.openNewLesson()
      },
      {
        icon: 'payment',
        label: 'Process Payment',
        color: 'success',
        permission: 'payments_collect',
        action: () => this.openPayment()
      },
      {
        icon: 'school',
        label: 'New Enrollment',
        color: 'info',
        permission: 'enrollments_create',
        action: () => this.openEnrollment()
      },
      {
        icon: 'assessment',
        label: 'View Reports',
        color: 'warning',
        permission: 'reports_ami',
        action: () => this.navigateToReports()
      },
      {
        icon: 'calendar_today',
        label: 'Today\'s Schedule',
        color: 'secondary',
        permission: 'calendar_view',
        action: () => this.navigateToDayView()
      }
    ].filter(action => this.isActionVisible(action));
  }

  get hasVisibleActions(): boolean {
    return this.quickActions.length > 0;
  }

  executeAction(action: QuickAction): void {
    if (!this.isActionVisible(action)) {
      return;
    }
    this.actionClicked.emit(action.label);
    action.action();
  }

  private isActionVisible(action: QuickAction): boolean {
    return !action.permission || this.permissionsService.canAccess(action.permission);
  }

  private openNewInquiry(): void {
    // Open the inquiry dialog, same as in day-view
    this.dialogService.openDialog(StudentInquiryEntityComponent, 'defaultWithData', {
      data: {
        EntityID: null,
        eckType: 'student_accounts',
        bundle: 'student_account',
        action: 'create',
        fieldsData: {
          'inquiry': true
        }
      }
    })
    .afterClosed()
    .subscribe(result => {
      if (result) {
        LoggerService.log('Inquiry created:', result);
      }
    });
  }

  private openEnrollment(): void {
    this.dialogService.openDialog(EnrollmentEntityComponent, 'enrollmentFormWithData', {
      data: {
        EntityID: null,
        eckType: 'enrollment',
        bundle: 'enrollment',
        action: 'create'
      }
    })
    .afterClosed()
    .subscribe(result => {
      if (result) {
        LoggerService.log('Enrollment created:', result);
      }
    });
  }

  private openPayment(): void {
    this.dialogService.openDialog(PaymentsEntityComponent, 'paymentFormWithData', {
      data: {
        EntityID: null,
        eckType: 'payment',
        bundle: 'payment',
        action: 'create'
      }
    })
    .afterClosed()
    .subscribe(result => {
      if (result) {
        LoggerService.log('Payment processed:', result);
      }
    });
  }

  private openNewLesson(): void {
    // Open the new lesson dialog, same as in day-view
    this.dialogService.openDialog(EventLessonEntityComponent, 'defaultWithData', {
      data: {
        EntityID: null,
        eckType: 'events',
        bundle: 'lessons',
        action: 'create',
        fieldsData: ''
      }
    })
    .afterClosed()
    .subscribe(result => {
      if (result) {
        LoggerService.log('Lesson scheduled:', result);
        // Optionally navigate to day-view to see the new lesson
        // this.router.navigate(['/day-view']);
      }
    });
  }

  private navigateToDayView(): void {
    // Navigate to day-view to see today's schedule
    this.router.navigate(['/day-view']);
  }

  private navigateToReports(): void {
    this.router.navigate(['/ami-reports']);
  }
}
